import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {
  Modal,
  Text,
  StyleSheet,
  View,
  ViewStyle,
  Dimensions,
  Pressable,
} from 'react-native';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountApi from 'src/api/AccountApi';
import HttpClient from 'src/api/HttpClient';
import GreetingsAndBalanceView from 'src/components/elements/home/GreetingsAndBalanceView';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import LeftArrow from 'src/components/img/svg/LeftArrow';
import RightArrow from 'src/components/img/svg/RightArrow';
import ArrowStep1 from 'src/components/img/svg/tutorials/ArrowStep1';
import ArrowStep2 from 'src/components/img/svg/tutorials/ArrowStep2';
import ArrowStep3 from 'src/components/img/svg/tutorials/ArrowStep3';
import ArrowStep4 from 'src/components/img/svg/tutorials/ArrowStep4';
import PayTab from 'src/components/img/svg/tutorials/PayTab';
import Styles from 'src/components/Styles';
import AppEventTypes from 'src/constants/AppEventTypes';
import Localized from 'src/constants/AppStrings';
import Logger from 'src/logging/Logger';
import {useAppSelector} from 'src/redux/hooks';
import Settings from 'src/Settings';
import AccountStore from 'src/stores/AccountStore';
import Util from 'src/Util';
import NetInfo from '@react-native-community/netinfo';
import {getAppTourDescriber} from './descriptor/DescriptorType';
import ArrowStep5 from 'src/components/img/svg/tutorials/ArrowStep5';
import QRCode from 'react-native-qrcode-svg';

enum TutorialSteps {
  NewPayTab = 'NewPayTab',
  AutoReloadGreetings = 'AutoReloadGreetings',
  NotificationsCenter = 'NotificationsCenter',
  LinkLocation = 'LinkLocation',
  QuickCheckout = 'QuickCheckout',
  AutoReloadQuickFund = 'AutoReloadQuickFund',
}

const InteractiveTutorial = () => {
  const width = Dimensions.get('window').width;
  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showInteractiveTour, setShowInteractiveTour] =
    useState<boolean>(false);
  const account = useAppSelector((s) => s.account.account);
  const heightRatio = Dimensions.get('screen').height >= 700 ? 1 : 0.865;

  useEffect(() => {
    getAppTourDetails();
  }, []);

  const getAppTourDetails = async () => {
    let appTourVersion = account.appTour;
    if (!appTourVersion || appTourVersion === '') {
      let res;
      try {
        res = await AccountApi.fetchAccount(AccountStore.getAccountId());
        appTourVersion = res?.appTour;
        Logger.Log.LogAPIEvent(
          'AccountAPI',
          'FetchAccount',
          JSON.stringify({accountId: AccountStore.getAccountId()}),
          JSON.stringify(res),
        );
      } catch (err) {
        Logger.Log.LogAPIEvent(
          'AccountAPI',
          'FetchAccount',
          JSON.stringify({accountId: AccountStore.getAccountId()}),
          JSON.stringify(err),
        );
      }
    }
    const networkState = await NetInfo.fetch();
    const isNetworkConnected = networkState?.isConnected;
    const appTourData = HttpClient.parseJsonSafe(appTourVersion);
    if (appTourData?.AppTourShown === Settings.appTourVersion) {
      setShowInteractiveTour(false);
    } else if (!isNetworkConnected) {
      setShowInteractiveTour(false);
    } else {
      setShowInteractiveTour(true);
    }
  };

  const getStepDetails = () => {
    switch (currentStep) {
      case 1:
        return Settings.isRefiveAnd365Pay()
          ? TutorialSteps.NewPayTab
          : TutorialSteps.QuickCheckout;
      case 2:
        return Settings.isRefiveAnd365Pay()
          ? TutorialSteps.AutoReloadGreetings
          : TutorialSteps.AutoReloadQuickFund;
      case 3:
        return TutorialSteps.NotificationsCenter;
      case 4:
        return TutorialSteps.LinkLocation;
    }
  };

  const updateAppTourStatus = async () => {
    //Hide Interactive Tutorial Moadal
    setShowInteractiveTour(false);

    try {
      const appTourVersionData = JSON.stringify({
        AppTourShown: Settings.appTourVersion,
      });
      const accountId = AccountStore.getAccountId();
      const utcDate = Util.getCurrentUTCDate();
      const date = Util.getCurrentDate();

      const result = await ActionsFactory.getAccountActions().updateAppEvent(
        accountId,
        AppEventTypes.AppTour,
        utcDate,
        date,
        moment.tz.guess(),
        appTourVersionData,
      );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'UpdateAppEvent',
        JSON.stringify({
          accountId: accountId,
          AppEvent: AppEventTypes.AppTour,
          appTourVersionData,
        }),
        JSON.stringify(result),
      );
    } catch (err) {
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'UpdateAppEvent',
        JSON.stringify({
          accountId: AccountStore.getAccountId(),
          AppEvent: AppEventTypes.AppTour,
        }),
        JSON.stringify(err),
      );
    }
  };

  const getStepTitle = () => {
    switch (getStepDetails()) {
      case TutorialSteps.NewPayTab:
        return Localized.Labels.new_pay_tab;
      case TutorialSteps.AutoReloadGreetings:
        return Localized.Labels.auto_reload_new_header;
      case TutorialSteps.NotificationsCenter:
        return Localized.Labels.notifications_center;
      case TutorialSteps.LinkLocation:
        return Localized.Labels.link_location;
      case TutorialSteps.QuickCheckout:
        return Localized.Labels.quick_checkout;
      case TutorialSteps.AutoReloadQuickFund:
        return Localized.Labels.auto_reload_new_header;
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (getStepDetails()) {
      case TutorialSteps.NewPayTab:
        return Localized.Labels
          .scan_your_unique_qr_code_to_make_quick_and_secure_payments;
      case TutorialSteps.AutoReloadGreetings:
        return Localized.Labels.revolve_auto_reload_description;
      case TutorialSteps.NotificationsCenter:
        return Localized.Labels
          .we_help_keep_you_updated_with_important_messages_and_updates;
      case TutorialSteps.LinkLocation:
        return Localized.Labels
          .choose_a_nearby_location_to_help_us_provide_you_with_more_relevant_features_in_the_app;
      case TutorialSteps.QuickCheckout:
        return Localized.Labels
          .checkout_is_a_breeze_when_you_scan_your_qr_to_pay_at_kiosk;
      case TutorialSteps.AutoReloadQuickFund:
        return Localized.Labels.revolve_auto_reload_description;
      default:
        return '';
    }
  };

  const getTextWidth = () => {
    switch (getStepDetails()) {
      case TutorialSteps.NewPayTab:
        return 160;
      case TutorialSteps.AutoReloadGreetings:
        return 200;
      case TutorialSteps.NotificationsCenter:
        return 230;
      case TutorialSteps.LinkLocation:
        return 220;
      case TutorialSteps.QuickCheckout:
        return 185;
      case TutorialSteps.AutoReloadQuickFund:
        return 200;
      default:
        return 160;
    }
  };

  const nextAction = () => {
    setCurrentStep(currentStep + 1);
  };

  const skipAction = () => {
    updateAppTourStatus();
  };

  const getActionButtons = () => {
    if (currentStep !== totalSteps) {
      return (
        <View style={styles.bottomButtonsView}>
          <RoundedAppButton
            accessibilityLabelValue={Localized.Buttons.skip}
            aria-label={Localized.Buttons.skip}
            titleText={Localized.Labels.skip}
            buttonType={RoundedAppButtonType.Solid}
            textStyle={
              getAppTourDescriber().appTourScreenStyles().btnBottomViewText
            }
            buttonViewStyle={{
              height: 42,
              minWidth: 80,
              backgroundColor: Styles.transparent,
            }}
            onPress={() => skipAction()}
            buttonTextColor={getAppTourDescriber().skipBtnColor()}
          />
          <RoundedAppButton
            accessibilityLabelValue={Localized.Buttons.next}
            aria-label={Localized.Buttons.next}
            titleText={Localized.Buttons.next}
            buttonType={RoundedAppButtonType.Solid}
            textStyle={
              getAppTourDescriber().appTourScreenStyles().nextBtnTextView
            }
            buttonViewStyle={{height: 42, minWidth: 80}}
            onPress={() => nextAction()}
          />
        </View>
      );
    } else {
      return (
        <View
          style={[
            styles.bottomButtonsView,
            {alignItems: 'center', justifyContent: 'center'},
          ]}
        >
          <RoundedAppButton
            accessibilityLabelValue={Localized.Buttons.lets_go_new}
            aria-label={Localized.Buttons.lets_go_new}
            titleText={Localized.Buttons.lets_go_new}
            buttonType={RoundedAppButtonType.Solid}
            buttonViewStyle={{height: 42, minWidth: 120}}
            onPress={() => skipAction()}
            textStyle={
              getAppTourDescriber().appTourScreenStyles().nextBtnTextView
            }
          />
        </View>
      );
    }
  };

  const stepTextView = (viewStyle: ViewStyle) => {
    return (
      <View
        style={[viewStyle, styles.textContentView, {width: getTextWidth()}]}
      >
        <Text
          nativeID={`Interactive_Tutorial_${getStepTitle()}`}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
          style={getAppTourDescriber().appTourScreenStyles().titleText}
        >
          {getStepTitle()}
        </Text>
        <Text
          nativeID={`Interactive_Tutorial_${getStepDescription()}`}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
          style={getAppTourDescriber().appTourScreenStyles().descriptionText}
        >
          {getStepDescription()}
        </Text>
      </View>
    );
  };

  const stepContent = () => {
    switch (getStepDetails()) {
      case TutorialSteps.NewPayTab:
        return (
          <View
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            {stepTextView({
              position: 'absolute',
              bottom: 194,
              left: 124,
            })}
            <View
              style={{
                position: 'absolute',
                bottom: 92,
                left: 16,
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 1 arrow pointing direction"
            >
              <ArrowStep1 />
            </View>
            <View
              style={{
                height: 54,
                width: 54,
                borderRadius: 27,
                backgroundColor: Styles.white,
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                bottom: 32,
                left: width / 5 + 8,
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="PayTab"
            >
              <PayTab />
            </View>
          </View>
        );

      case TutorialSteps.AutoReloadGreetings:
        return (
          <View
            style={{
              top: Styles.Heights.touchTargetHeight1 + 54,
              width: '100%',
              height: '100%',
            }}
          >
            <GreetingsAndBalanceView />
            <View
              style={{
                left: Styles.Spacing.m5,
                marginTop: 120,
                position: 'absolute',
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 2 arrow pointing direction"
            >
              <ArrowStep2 />
            </View>
            {stepTextView({
              left: Styles.Spacing.m5 + 85,
              marginTop: 185,
              position: 'absolute',
            })}
          </View>
        );

      case TutorialSteps.AutoReloadQuickFund:
        return (
          <View
            style={{
              top:
                (Styles.Heights.touchTargetHeight1 +
                  width * 0.75 -
                  Styles.Spacing.m3 * 2 +
                  20) *
                heightRatio,
              width: '100%',
              height: '100%',
            }}
          >
            <View
              style={{
                backgroundColor: Styles.white,
                borderRadius: 19,
                height: 38,
                width: 108,
                marginLeft: Styles.Spacing.m4,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
                nativeID={`Interactive_Tutorial_${Localized.Buttons.quick_fund}`}
                style={{
                  fontSize: Styles.Fonts.f8,
                  fontFamily: Styles.FontFamily.robotoRegular,
                  fontWeight: '700',
                  color: Styles.Colors.Canteen.darkGreen,
                }}
                accessibilityLabel={Localized.Buttons.quick_fund}
                aria-label={Localized.Buttons.quick_fund}
              >
                {Localized.Buttons.quick_fund}
              </Text>
            </View>
            <View
              style={{
                left: Styles.Spacing.m5,
                marginTop: 50 * heightRatio,
                position: 'absolute',
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 2 arrow pointing direction"
            >
              <ArrowStep2 />
            </View>
            {stepTextView({
              left: Styles.Spacing.m5 + 85,
              marginTop: 132 * heightRatio,
              position: 'absolute',
            })}
          </View>
        );

      case TutorialSteps.QuickCheckout:
        return (
          <View
            style={{
              top: (Styles.Heights.touchTargetHeight1 + 80) * heightRatio,
              width: '100%',
              height: '100%',
            }}
          >
            <View style={{width: '100%', alignItems: 'center'}}>
              <View style={[styles.qrCodeInnerView, {width: width / 3 + 16}]}>
                <QRCode
                  value={AccountStore.getQrCode() ?? ''}
                  size={width / 3}
                />
              </View>
            </View>
            <View
              style={{
                width: width,
                marginTop: 164 * heightRatio,
                position: 'absolute',
                alignItems: 'center',
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 5 arrow pointing direction"
            >
              <ArrowStep5 />
            </View>
            {stepTextView({
              left: Styles.Spacing.m4,
              marginTop: 304 * heightRatio,
              position: 'absolute',
            })}
          </View>
        );

      case TutorialSteps.NotificationsCenter:
        return (
          <View
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <View
              style={[
                {
                  height: 54,
                  borderRadius: 27,
                  width: 54,
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: Styles.Heights.touchTargetHeight1 + 4,
                  right: 8,
                },
                getAppTourDescriber().appTourScreenStyles()
                  .notificationIconView,
              ]}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Notification Icon"
            >
              {getAppTourDescriber().appTourNotificationIcon()}
            </View>
            <View
              style={{
                marginTop: Styles.Heights.touchTargetHeight1 + 24,
                right: 62,
                position: 'absolute',
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 3 arrow pointing direction"
            >
              <ArrowStep3 />
            </View>
            {stepTextView({
              right: Styles.Spacing.m4 + Styles.Spacing.m3,
              marginTop: Styles.Heights.touchTargetHeight1 + 172,
              position: 'absolute',
            })}
          </View>
        );

      case TutorialSteps.LinkLocation:
        return (
          <View
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <View
              style={{
                position: 'absolute',
                top: Styles.Heights.touchTargetHeight2,
                left: 8,
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Location Drop Down"
            >
              {getAppTourDescriber().appTourLocationNotificationIcon()}
            </View>

            <View
              style={{
                left: Styles.Spacing.m4 + Styles.Spacing.m2,
                marginTop: 108,
                position: 'absolute',
              }}
              accessible={true}
              accessibilityRole="image"
              accessibilityLabel="Step 4 arrow pointing direction"
            >
              <ArrowStep4 />
            </View>
            {stepTextView({
              left: Styles.Spacing.m5 + 72,
              marginTop: 204,
              position: 'absolute',
            })}
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      transparent={true}
      style={{zIndex: 1}}
      visible={showInteractiveTour}
      animationType="fade"
      statusBarTranslucent={true}
    >
      <View style={[styles.overlay, {backgroundColor: 'rgba(92,92,101,0.95)'}]}>
        {stepContent()}
        <View style={styles.stepIndicatorView}>
          <Pressable
            style={styles.arrowButtonView}
            disabled={currentStep === 1}
            onPress={() => setCurrentStep(currentStep - 1)}
            nativeID={'ArrowButtonView_LeftArrow_' + currentStep}
            accessibilityLabel={'ArrowButtonView LeftArrow'}
            aria-label="ArrowButtonView LeftArrow"
          >
            {currentStep !== 1 && <LeftArrow />}
          </Pressable>
          <Text
            nativeID={`Interactive_Tutorial_${Localized.Labels.formatString(
              Localized.Labels.steps_tutorials,
              currentStep,
              totalSteps,
            )}`}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
            style={
              getAppTourDescriber().appTourScreenStyles().stepIndicatorText
            }
          >
            {Localized.Labels.formatString(
              Localized.Labels.steps_tutorials,
              currentStep,
              totalSteps,
            )}
          </Text>
          <Pressable
            nativeID={'ArrowButtonView_RightArrow_' + currentStep}
            style={styles.arrowButtonView}
            disabled={currentStep === totalSteps}
            onPress={() => setCurrentStep(currentStep + 1)}
            accessibilityLabel={'ArrowButtonView RightArrow'}
            aria-label="ArrowButtonView RightArrow"
          >
            {currentStep !== totalSteps && <RightArrow />}
          </Pressable>
        </View>
        {getActionButtons()}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(74,73,83,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomButtonsView: {
    position: 'absolute',
    left: Styles.Spacing.m3,
    right: Styles.Spacing.m3,
    bottom: 24,
    height: Styles.Heights.h5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepIndicatorView: {
    position: 'absolute',
    left: Styles.Spacing.m3,
    right: Styles.Spacing.m3,
    bottom: 104,
    height: Styles.Heights.h5,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },

  arrowButtonView: {
    width: Styles.Heights.touchTargetHeight1,
    height: Styles.Heights.touchTargetHeight1,
    marginHorizontal: Styles.Spacing.m2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  textContentView: {
    width: 160,
    alignItems: 'center',
  },
  qrCodeInnerView: {
    padding: Styles.Spacing.m2,
    backgroundColor: Styles.white,
    borderRadius: 5,
  },
});

export default InteractiveTutorial;
